import { StablecoinProvider } from './stablecoin.model';

export enum StablecoinTransferType {
	deposit = 'deposit',
	withdrawalFiat = 'withdrawal_fiat',
	withdrawalCrypto = 'withdrawal_crypto',
	conversion = 'conversion',
	internal = 'internal',
}

export enum StablecoinTransferStatus {
	pendingApproval = 'pending_approval',
	approved = 'approved',
	rejected = 'rejected',
	expired = 'expired',
	canceled = 'canceled',
	submitting = 'submitting',
	processing = 'processing',
	completed = 'completed',
	failed = 'failed',
}

export enum StablecoinApprovalDecisionValue {
	approve = 'approve',
	reject = 'reject',
}

/** Workflow pipeline stage a transfer can sit in. */
export enum StablecoinTransferStage {
	initiate = 'initiate',
	review = 'review',
	approve = 'approve',
	release = 'release',
}

export const TRANSFER_STAGE_LABELS: Record<StablecoinTransferStage, string> = {
	[StablecoinTransferStage.initiate]: 'Initiate',
	[StablecoinTransferStage.review]: 'Review',
	[StablecoinTransferStage.approve]: 'Approve',
	[StablecoinTransferStage.release]: 'Release',
};

/** Per-stage requirements snapshot inside a transfer's stage context. */
export interface StablecoinStageContextStage {
	participants: string[];
	required: number;
	required_approvers: string[];
	matched_rules: string[];
}

/** Workflow routing snapshot attached to a transfer governed by a workflow. */
export interface StablecoinStageContext {
	order: string[];
	stages: { [stage: string]: StablecoinStageContextStage };
}

export interface StablecoinTransfer {
	id: string;
	organization_id: string;
	account_id: string;
	type: StablecoinTransferType;
	status: StablecoinTransferStatus;
	source_asset: string | null;
	source_amount: string | null;
	destination_asset: string | null;
	destination_amount: string | null;
	counterparty_account_id: string | null;
	destination_id: string | null;
	network: string | null;
	provider: StablecoinProvider;
	provider_ref: string | null;
	initiated_by: string | null;
	initiated_by_email: string | null;
	failure_reason: string | null;
	workflow_id: string | null;
	current_stage: StablecoinTransferStage | null;
	stage_context: StablecoinStageContext | null;
	created_at: string;
	submitted_at: string | null;
	completed_at: string | null;
}

export interface StablecoinTransferCreate {
	organization_id?: string | null;
	account_id: string;
	type: StablecoinTransferType;
	asset: string;
	amount: string;
	destination_asset?: string | null;
	counterparty_account_id?: string | null;
	destination_id?: string | null;
	network?: string | null;
}

export interface StablecoinApprovalDecision {
	approver_subject: string;
	decision: StablecoinApprovalDecisionValue;
	comment: string | null;
	created_at: string;
}

/** One approval request on a transfer. stage is null for legacy single-policy approvals. */
export interface StablecoinApprovalState {
	stage: StablecoinTransferStage | null;
	required_count: number;
	status: string;
	initiated_by: string | null;
	expires_at: string | null;
	decisions: StablecoinApprovalDecision[];
}

export interface StablecoinApprovalPolicy {
	id: string;
	organization_id: string;
	auto_approve_under: string | null;
	required_approvals: number;
	allowlist_only: boolean;
	created_at: string;
}

export interface TransferStatusDisplay {
	label: string;
	cssClass: 'status-pending' | 'status-active' | 'status-success' | 'status-failed' | 'status-muted';
}

export const TRANSFER_STATUS_DISPLAY: Record<StablecoinTransferStatus, TransferStatusDisplay> = {
	[StablecoinTransferStatus.pendingApproval]: { label: 'Pending approval', cssClass: 'status-pending' },
	[StablecoinTransferStatus.approved]: { label: 'Approved', cssClass: 'status-active' },
	[StablecoinTransferStatus.rejected]: { label: 'Rejected', cssClass: 'status-failed' },
	[StablecoinTransferStatus.expired]: { label: 'Expired', cssClass: 'status-muted' },
	[StablecoinTransferStatus.canceled]: { label: 'Canceled', cssClass: 'status-muted' },
	[StablecoinTransferStatus.submitting]: { label: 'Submitting', cssClass: 'status-active' },
	[StablecoinTransferStatus.processing]: { label: 'Processing', cssClass: 'status-active' },
	[StablecoinTransferStatus.completed]: { label: 'Completed', cssClass: 'status-success' },
	[StablecoinTransferStatus.failed]: { label: 'Failed', cssClass: 'status-failed' },
};

export const TRANSFER_TYPE_LABELS: Record<StablecoinTransferType, string> = {
	[StablecoinTransferType.deposit]: 'Deposit',
	[StablecoinTransferType.withdrawalFiat]: 'Withdrawal · wire',
	[StablecoinTransferType.withdrawalCrypto]: 'Withdrawal · crypto',
	[StablecoinTransferType.conversion]: 'Conversion',
	[StablecoinTransferType.internal]: 'Internal',
};

export const CANCELABLE_STATUSES: StablecoinTransferStatus[] = [StablecoinTransferStatus.pendingApproval, StablecoinTransferStatus.approved];
